import { CenterItem, RowAlignedItems } from '@components/LinearLayout';
import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import { Document, Page } from '@react-pdf/renderer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/Button';
import Spinner from '@components/Spinner';
import WeModal from '@components/WeModal';
import { ApplyButtonWrapper } from '@composites/Filters/styles';
import { DefaultBooleanValues } from '@composites/InboundButton';
import { getEventDetails } from '@store/slices/eventSlice';
import { SPEAKER_PAGE_WIDTH } from '@utils/constants';
import { safeJSONParse } from '@utils/fn';
import { printPdf } from '@utils/hooks';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import RegistrationForm from '../RegistrationForm';

interface DownloadWhitePaperProps {
  label: string;
  buttonType: string;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const DownloadWhitePaper = ({
  label,
  buttonType,
  inboundValues,
  eventId,
}: DownloadWhitePaperProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const { inboundCookie, actionInbound, registerInbound, shouldShowForm } =
    useInbound();
  const [loading, setLoading] = useState<boolean>(false);

  const generateWhitePaperPDF = useCallback(() => {
    const doc = (
      <Document>
        <Page size="A4" orientation="portrait"></Page>
      </Document>
    );
    setLoading(true);
    printPdf(doc, t('white-paper'))
      .catch(console.error)
      .finally(() => { setLoading(false); });
  }, [t]);

  const inputElements = useMemo(
    () =>
      orderedFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFields?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = safeJSONParse(inboundCookie)?.[key.id] ?? '';
      return acc;
    }, {});
  }, [inboundCookie, inputElements]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Clear error for a field when user starts typing in it
  const handleChange = (fieldName: string) => (value: string) => {
    clearErrors(fieldName);
  };

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'download_whitepaper',
        },
        () => {
          generateWhitePaperPDF();
          setShowModal(false);
        }
      );
    },
    [checkedApproval, registerInbound, inboundCookie, sluggedEvent?.id]
  );

  return (
    <>
      <Button
        icon={loading ? <Spinner size="2rem" /> : undefined}
        title={label}
        backgroundColor={sluggedEvent?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        border={`0.1rem solid ${theme.color.mobilityGrey}`}
        padding="1.1rem 3rem"
        width="max-content"
        disabled={loading}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              setShowModal(true);
            } else {
              generateWhitePaperPDF();

              await actionInbound({
                ...JSON.parse(inboundCookie ?? '{}'),
                event: sluggedEvent?.id,
                path: window.location.href,
                action: 'download_whitepaper',
              });
            }
          })();
        }}
      />
      <WeModal
        visible={showModal}
        maxHeight="94vh"
        backgroundColor={theme.color.pureWhite}
        width={`min(${SPEAKER_PAGE_WIDTH}, 90vw)`}
        onClose={() => {
          setShowModal(false);
        }}
        footer={
          <CenterItem>
            <ApplyButtonWrapper>
              <RowAlignedItems $gap="3rem">
                <Button
                  title={t('download')}
                  backgroundColor={sluggedEvent?.design.themeColor}
                  hoverBackgroundColor={theme.color.mobilityGrey}
                  textColor={theme.color.white}
                  borderRadius={theme.borderRadius.radius10}
                  padding="0.6rem 2.5rem"
                  width="max-content"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSubmit(onSubmit)}
                />
              </RowAlignedItems>
            </ApplyButtonWrapper>
          </CenterItem>
        }
      >
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <RegistrationForm
            control={control}
            errors={errors}
            inputElements={inputElements}
            setCheckedApproval={setCheckedApproval}
            checkedApproval={checkedApproval}
            handleChange={handleChange}
          />
        </form>
      </WeModal>
    </>
  );
};

export default DownloadWhitePaper;
